// ============================================================================
// OBT — Hata normalizasyonu
// ============================================================================
// Backend gövde şekli (backend/shared/exceptions.py):
//   { "error": { "type": "ObtPermissionError", "message": "...",
//                "details": {...} }, "request_id": "..." }
// 401 farklı şekillidir (HTTPException stringify edilir), bu yüzden 401'de
// gövdeye DEĞİL, HTTP durum koduna bakılır.
//
// GÜVENLİK: Backend hata metni kontrolsüz biçimde kullanıcıya dökülmez.
// Yalnız OBT alan hataları (`Obt*` tipleri) kürasyonlu Türkçe mesajlar
// içerdiği için gösterilir; diğer her şeyde (özellikle 5xx) sabit ve genel
// bir mesaj gösterilir. `details` hiçbir zaman doğrudan ekrana basılmaz.
// ============================================================================

export const OBT_ERROR_KIND = Object.freeze({
  UNAUTHENTICATED: 'unauthenticated',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  CONFLICT: 'conflict',
  VALIDATION: 'validation',
  RATE_LIMITED: 'rate_limited',
  SERVER: 'server',
  NETWORK: 'network',
  UNKNOWN: 'unknown',
});

const GENERIC_MESSAGES = Object.freeze({
  [OBT_ERROR_KIND.UNAUTHENTICATED]:
    'Oturumunuz sona ermiş görünüyor. Lütfen yeniden giriş yapın.',
  [OBT_ERROR_KIND.FORBIDDEN]: 'Bu OBT işlemi için yetkiniz bulunmuyor.',
  [OBT_ERROR_KIND.NOT_FOUND]:
    'Kayıt bulunamadı veya yetki kapsamınız dışında.',
  [OBT_ERROR_KIND.CONFLICT]:
    'Kayıt bu sırada başkası tarafından değiştirilmiş. Lütfen sayfayı yenileyip tekrar deneyin.',
  [OBT_ERROR_KIND.VALIDATION]:
    'Gönderilen bilgiler geçerli değil. Lütfen alanları kontrol edin.',
  [OBT_ERROR_KIND.RATE_LIMITED]:
    'Çok fazla işlem yaptınız. Lütfen biraz bekleyip tekrar deneyin.',
  [OBT_ERROR_KIND.SERVER]:
    'Sunucu tarafında bir sorun oluştu. Lütfen daha sonra tekrar deneyin.',
  [OBT_ERROR_KIND.NETWORK]:
    'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.',
  [OBT_ERROR_KIND.UNKNOWN]: 'Beklenmeyen bir hata oluştu.',
});

function kindForStatus(status) {
  if (status === 401) return OBT_ERROR_KIND.UNAUTHENTICATED;
  if (status === 403) return OBT_ERROR_KIND.FORBIDDEN;
  if (status === 404) return OBT_ERROR_KIND.NOT_FOUND;
  if (status === 409) return OBT_ERROR_KIND.CONFLICT;
  if (status === 400 || status === 422) return OBT_ERROR_KIND.VALIDATION;
  if (status === 429) return OBT_ERROR_KIND.RATE_LIMITED;
  if (typeof status === 'number' && status >= 500) return OBT_ERROR_KIND.SERVER;
  return OBT_ERROR_KIND.UNKNOWN;
}

/**
 * Axios hatasını OBT arayüzünün anlayacağı sabit bir şekle indirger.
 * @returns {{status: number|null, kind: string, message: string, errorType: string|null, requestId: string|null}}
 */
export function normalizeObtError(error) {
  const status = error?.response?.status ?? null;
  const body = error?.response?.data;

  if (status === null) {
    return {
      status: null,
      kind: OBT_ERROR_KIND.NETWORK,
      message: GENERIC_MESSAGES[OBT_ERROR_KIND.NETWORK],
      errorType: null,
      requestId: null,
    };
  }

  const kind = kindForStatus(status);
  const errorType =
    typeof body?.error?.type === 'string' ? body.error.type : null;
  const backendMessage =
    typeof body?.error?.message === 'string' ? body.error.message : null;

  // Yalnız OBT alan hataları güvenle gösterilebilir (kürasyonlu Türkçe metin).
  // 401 gövdesi stringify edilmiş bir sözlük olduğu için ASLA gösterilmez.
  const safeToShow =
    kind !== OBT_ERROR_KIND.UNAUTHENTICATED &&
    kind !== OBT_ERROR_KIND.SERVER &&
    errorType !== null &&
    errorType.startsWith('Obt') &&
    Boolean(backendMessage);

  return {
    status,
    kind,
    message: safeToShow ? backendMessage : GENERIC_MESSAGES[kind],
    errorType,
    requestId: typeof body?.request_id === 'string' ? body.request_id : null,
  };
}

/** Yetki reddi mi (panelde "erişim yok" görünümüne geçilir). */
export function isObtForbidden(normalized) {
  return normalized?.kind === OBT_ERROR_KIND.FORBIDDEN;
}

/** Oturum hatası mı (global auth akışı devralır; panel kendi başına yönlendirmez). */
export function isObtUnauthenticated(normalized) {
  return normalized?.kind === OBT_ERROR_KIND.UNAUTHENTICATED;
}
